import Link from "../../widgets/Link";
import styles from "../../../styles/partials/header-components/Nav.scss";
import { _classes, useCurrentBasePageContent } from "../../utilities/helpers";
import { caretArrow } from "../../widgets/SVGs/caretArrow";
import { useState } from "react";
import { motion } from "../../utilities/FramerMotion";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";

const cl = _classes(styles);

const slideVerticalAnimation = {
  open: {
    rotateX: 0,
    y: 0,
    opacity: 1,
    transition: { duration: 0.3, mass: 0.8, type: "linear" },
    display: "block",
  },
  close: {
    rotateX: -15,
    y: -10,
    opacity: 0,
    transition: { duration: 0.3 },
    transitionEnd: { display: "none" },
  },
};

const SubNav = ({ subSelections, isSubMenuOpen, toggleMenu }) => (
  <motion.ul
    initial="close"
    animate={isSubMenuOpen ? "open" : "close"}
    variants={slideVerticalAnimation}
    className={cl("subnav-wrapper")}
  >
    {subSelections.map((item) => (
      <li key={item?.id || item?.path} className={cl("subnav")}>
        <Link
          path={item.checkbox1 ? null : item.path}
          onClick={(e) =>
            item?.onClick ? item.onClick(e, toggleMenu) : toggleMenu()
          }
        >
          {item.linktitle}
        </Link>
      </li>
      ))}
  </motion.ul>
);

const NavItem = ({
  item,
  isSubMenuOpen,
  toggleSubMenu,
  propertyPage,
  toggleMenu,
  history,
}) => {
  const templateIsGuestRooms = item.mainNav.template === "guest_rooms";
  const templateIsEatDrink = item.mainNav.template === "eat_and_drink";
  const isOffersOrEatAndDrink = ["offers", "eat_and_drink"].includes(
    item.mainNav.template
  );
  const isEventsItem = item.mainNav.isEventsItem;

  const itemHasSubNav =
    item.subNav.length > 0 && item.mainNav.template !== "offers"; // If nav item has children and is not children of offers
  const thePath = templateIsGuestRooms
    ? null //if its guest rooms, return null
    : isOffersOrEatAndDrink || isEventsItem
    ? item.mainNav.path //if its offers or eat and drink or events, return the path
    : itemHasSubNav
    ? null //if there is a subnav, don't pass the path as we don't want to navigate to the page but instead open the subnav
    : item.mainNav.path || "/"; //Last, return the path if there is one, otherwise return the root path
  // console.log("Item", item);
  return (
    <li key={item.mainNav.id} className={itemHasSubNav ? cl("has-subnav") : ""}>
      <div className={cl("wrapper")}>
        <Link
          className={cl("nav-item")}
          path={thePath}
          hash={isEventsItem && item.mainNav.hash ? item.mainNav.hash : null}
          onClick={() => {
            if (templateIsEatDrink || templateIsGuestRooms) {
              history.push({
                hash: "property-rooms",
                pathname: propertyPage.path,
              });
              //Slide down the page to the div with the id of property-rooms
              document.getElementById("property-rooms").scrollIntoView({
                behavior: "smooth",
                offset: 100,
              });
              toggleMenu();
            } else if (isEventsItem) {
              // If there's a hash, use it for routing
              if (item.mainNav.hash) {
                history.push({
                  hash: item.mainNav.hash,
                  pathname: item.mainNav.path,
                });
              } else {
                // Otherwise just navigate to the path
                history.push(item.mainNav.path);
              }
              toggleMenu();
            } else if (itemHasSubNav) {
              toggleSubMenu(item.mainNav.id);
            } else {
              toggleMenu();
            }
          }}
        >
          {item.mainNav.title}
          {itemHasSubNav &&
            !["offers", "eat_and_drink", "guest_rooms", "events"].includes(
              item.mainNav.template
            ) && (
              <div
                className={cl(["icon", isSubMenuOpen ? "open" : ""])}
                onClick={() => toggleSubMenu(item.mainNav.id)}
              >
                {caretArrow}
              </div>
            )}
        </Link>
      </div>
      {itemHasSubNav && (
        <SubNav
          subSelections={item.subNav}
          isSubMenuOpen={isSubMenuOpen}
          toggleMenu={toggleMenu}
        />
      )}
    </li>
  );
};

NavItem.propTypes = {
  item: PropTypes.object,
  isSubMenuOpen: PropTypes.bool,
  toggleSubMenu: PropTypes.func,
  propertyPage: PropTypes.object,
  toggleMenu: PropTypes.func,
  history: PropTypes.object,
};

SubNav.propTypes = {
  subSelections: PropTypes.array,
  isSubMenuOpen: PropTypes.bool,
  toggleMenu: PropTypes.func,
};

const Nav = ({ selections, toggleMenu }) => {
  const history = useHistory();
  const { basePageContent: propertyPage } = useCurrentBasePageContent();

  const isHelena = propertyPage?.slug?.includes("helena");
  const [subNavOpen, setSubNavOpen] = useState("");

  // Extract events page if it exists in any subnav
  let eventsItem = null;
  let eventsParentIndex = -1;

  // Find the events item in subNav
  for (let i = 0; i < selections?.length; i++) {
    const item = selections[i];
    if (item?.subNav?.length) {
      const eventIndex = item.subNav.findIndex((sub) => sub?.slug === "events");
      if (eventIndex !== -1) {
        eventsItem = item.subNav[eventIndex];
        eventsParentIndex = i;
        break;
      }
    }
  }

  // If events page found, move it to main navigation
  if (eventsItem && eventsParentIndex !== -1) {
    const updatedSelections = [...selections];

    // Remove from original subnav
    updatedSelections[eventsParentIndex] = {
      ...updatedSelections[eventsParentIndex],
      subNav: updatedSelections[eventsParentIndex].subNav.filter(
        (sub) => sub.slug !== "events"
      ),
    };

    // Parse the h2 value which contains the full path with hash
    const eventFullPath = eventsItem.h2 || "";
    const [pathPart, hashPart] = eventFullPath.split("#");

    // Create new main nav item
    const eventsMainNavItem = {
      mainNav: {
        id: `events-${eventsItem.id || Date.now()}`,
        title: eventsItem.linktitle || eventsItem.title,
        path: pathPart, // Use the path part from h2
        template: "events",
        isEventsItem: true, // Flag to identify this as events item
        hash: hashPart ? `${hashPart}` : "", // Use the hash part from h2
      },
      subNav: [],
    };

    // Insert before the last item
    const position = updatedSelections.length - 1;
    updatedSelections.splice(position, 0, eventsMainNavItem);

    selections = updatedSelections;
  }

  const toggleSubMenu = (id) => setSubNavOpen(subNavOpen === id ? "" : id);

  return (
    <nav className={cl("root")}>
      <ul>
        {selections &&
          selections.map((item) => {
            // console.log("%c item", "color: deeppink", item);
            return (
              <NavItem
                key={item.mainNav.id}
                item={item}
                isSubMenuOpen={subNavOpen === item.mainNav.id}
                toggleSubMenu={toggleSubMenu}
                propertyPage={propertyPage}
                toggleMenu={toggleMenu}
                history={history}
              />
            );
          })}
      </ul>
    </nav>
  );
};

Nav.propTypes = {
  selections: PropTypes.array,
  toggleMenu: PropTypes.func,
};

export default Nav;
